import { chunk, filter, find, map, orderBy, reject, some } from 'lodash';
import { humanizeTenantId } from '@a0/support-center-common/lib/tenants';

export function ownTenants(tenants) {
  return filter(tenants, { role: 'admin' });
}

export function ownTenantsForReactSelect(tenants) {
  return ownTenants(tenants).map(tenant => ({
    value: tenant.id,
    label: humanizeTenantId(tenant.id)
  }));
}

export function tenantsFormattedForDropdown(tenants) {
  return tenants.map(tenant => {
    return {
      value: tenant.id,
      text: humanizeTenantId(tenant.id)
    };
  });
}

export function ownTenantsFormattedForDropdown(tenants) {
  return tenantsFormattedForDropdown(ownTenants(tenants));
}

export const filterByFiltering = (records, filtering) => {
  return reject(records, record =>
    some(filtering, (filterItem, key) => filterItem.includes(record[key]))
  );
};

export const filterRecordsByPage = (records, activePage, size = 15) => {
  const pages = chunk(records, size);
  const recordsByPage = pages[+activePage - 1] || [];
  return {
    activePage,
    recordsByPage,
    pageCount: pages.length
  };
};

export const filterByIds = (entity, ids) => {
  const records = [];

  for (const id of ids) {
    const record = entity[id];
    if (record) records.push({ id, ...record });
  }

  return records;
};

export const getSelectedTenant = tenantState =>
  tenantState.records[tenantState.selected];

const getFreeType = t => !t.subscription.isPaying && !t.subscription.isTrial;
const getTrialType = t => !t.subscription.isPaying && t.subscription.isTrial;

export const getDeveloperType = t =>
  !t.subscription.isCustomAgreement && !getFreeType(t) && !getTrialType(t);

const getWithTrialDatesType = t =>
  !t.subscription.isPaying &&
  t.subscription.trialDates &&
  t.subscription.trialDates.trialStarts &&
  t.subscription.trialDates.trialEnds;

const filterByTenantType = (t, type) => {
  if (type === 'Developer') {
    return getDeveloperType(t);
  } else if (type === 'Free') {
    return getFreeType(t);
  } else if (type === 'Trial') {
    return getTrialType(t);
  }

  return t.master_tenant === type;
};

export const filterTenantsBySubscription = ({ tenants, tenantAgreements }) =>
  filter(tenants.records, t =>
    filterByTenantType(t, tenantAgreements.selected)
  );

export const filterTenantIdsBySubscription = state =>
  map(filterTenantsBySubscription(state), t => t.id);

export const getTenantSummaries = state => {
  const tenants = filterTenantsBySubscription(state);
  const tenantSummaries = tenants.map(tenant => {
    const summary = state.tenantSummaries.records[tenant.id];

    return { ...tenant, ...summary };
  });

  return orderBy(
    tenantSummaries,
    [
      'active_users_last_30d',
      'total_users',
      'total_apps',
      'total_connections',
      'total_rules',
      'active_rules',
      'tenant'
    ],
    ['desc', 'desc', 'desc', 'desc', 'desc', 'desc', 'asc']
  );
};

export const getTenantsForSubscription = (
  { tenants },
  subscriptionMasterTenant
) =>
  filter(tenants.records, t => filterByTenantType(t, subscriptionMasterTenant));

export const getTenantsWithCustomAgreements = state =>
  filter(state.tenants.records, t => t.subscription.isCustomAgreement);

export const getDeveloperTenants = state =>
  filter(state.tenants.records, t => getDeveloperType(t));

export const getFreeTenants = state =>
  filter(state.tenants.records, t => getFreeType(t));

export const getTrialTenants = state =>
  filter(state.tenants.records, t => getTrialType(t));

export const getWithTrialDatesTenants = state =>
  filter(state.tenants.records, t => getWithTrialDatesType(t));

export const getOwnDeveloperTenants = state =>
  reject(getDeveloperTenants(state), t => t.role === 'guest');

export const selectDefaultTenant = state =>
  getTenantsWithCustomAgreements(state)[0] ||
  getDeveloperTenants(state)[0] ||
  getTrialTenants(state)[0] ||
  getFreeTenants(state)[0];

export const getNotificationFilterTenant = state =>
  find(
    filterTenantsBySubscription(state),
    tenant => tenant.id === state.notifications.filterOption
  ) || { id: 'all', name: state.tenants.selected };

export const filterNotificationByTenant = (
  { records, filterOption },
  tenants
) => {
  return filter(records, notification => {
    return (
      !notification.hasOwnProperty('targetTenants') ||
      (notification.hasOwnProperty('targetTenants') &&
        find(notification.targetTenants, tenant => {
          if (filterOption && filterOption !== 'all') {
            return filterOption === tenant;
          }
          return some(tenants, ['id', tenant]);
        }))
    );
  });
};

export function getNotifications(state) {
  const tenants = filterTenantsBySubscription(state);
  return filterNotificationByTenant(state.notifications, tenants) || [];
}

export const getLoadingStatusForTenantsDropdown = ({
  tenantAgreements,
  tenants
}) => tenantAgreements.loading || tenants.loading;

export const sendToForum = ({ auth }) => {
  return (
    !auth.isAuthenticated || (!auth.data.paying && !auth.data.trial) || false
  );
};

export const hasMasterAccess = state => {
  const currentTenant = getSelectedTenant(state.tenants);

  if (currentTenant && currentTenant.master_tenant) {
    const tenants = filterTenantsBySubscription(state);
    return (
      tenants.filter(
        tenant =>
          `${tenant.name}@${tenant.region}` === currentTenant.master_tenant
      ).length > 0
    );
  }

  return true;
};

export const getMasterRegion = state => {
  const currentTenant = getSelectedTenant(state.tenants);
  return (
    currentTenant &&
    ((currentTenant.master_tenant &&
      currentTenant.master_tenant.split('@')[1]) ||
      currentTenant.region)
  );
};

export const getSelectedSupportPlan = ({ tenants }) => {
  const selectedTenant = tenants.records[tenants.selected];
  return selectedTenant ? selectedTenant.supportPlan : null;
};
